import { cn } from '@/shared/helpers/classnames'
import { Button } from '@/shared/ui/button'

import { type FC, useCallback } from 'react'
import { XLg } from 'react-bootstrap-icons'
import { useTranslation } from 'react-i18next'

import { Close as RadixDialogClose, Title as RadixDialogTitle } from '@radix-ui/react-dialog'

import type { DialogHeaderProps } from './types'
import { useDialogContext } from './useDialogContext'

/**
 * Dialog Header component
 */
export const DialogHeader: FC<DialogHeaderProps> = ({ title }) => {
  const { variant, onClose, cancelCallback } = useDialogContext()
  const { t } = useTranslation()

  const handleCloseClick = useCallback(
    (event: React.MouseEvent) => {
      if (cancelCallback) {
        cancelCallback(event)
      } else {
        onClose()
      }
    },
    [cancelCallback, onClose],
  )

  const isTextTitle = typeof title === 'string'

  return (
    <div
      className={cn('dialog-header', {
        'dialog-header-default': variant === 'default',
        'dialog-header-error': variant === 'error',
      })}
    >
      {title && (
        <RadixDialogTitle
          className={cn('dialog-header-title', {
            'dialog-header-title-multiline': !isTextTitle,
          })}
        >
          {title}
        </RadixDialogTitle>
      )}
      <RadixDialogClose asChild>
        <Button
          onClick={handleCloseClick}
          variant="link"
          size="sm"
          intent="accent"
          icon={{ component: XLg }}
          aria-label={t('common.close')}
        />
      </RadixDialogClose>
    </div>
  )
}

DialogHeader.displayName = 'DialogHeader'
